<script setup lang="ts">
import type { CategoryType } from './types';
import type {
	BreadcrumbItem,
	BreadcrumbItemWithIndex
} from '~/components/ui/breadcrumb';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { SectionName } from '~/types/mixpanel';
import type { RouteLocationRaw } from 'vue-router';

import { useEvents } from '~/composables/events/useEvents';
import { usePropertyFilters } from '~/composables/propertySearch/context';
import { useRegionPropertiesLinking } from '~/composables/useRegionPropertiesLinking';
import {
	PropertyTypeEnum,
	PropertyTypePlurals
} from '~/core/entities/CRMEnums';

import TextLink from '../ui/text-link/TextLink.vue';
import { createRoomFilterMapper } from './types';

const propertyFiltersApi = usePropertyFilters();

interface LinkData {
	route: RouteLocationRaw;
	label: string;
	amount?: number;
}

interface TypologyData {
	type: string;
	links: LinkData[];
}

const props = defineProps<{
	propertyType: PropertyTypeEnum;
	region: string;
	city: string;
	state: string;
	sectionName?: SectionName;
	breadcrumbItems?: BreadcrumbItem[];
}>();

const emit = defineEmits<{
	(e: 'filter-link-clicked', filters: PropertyFilters): void;
	(e: 'breadcrumbClicked', breadcrumbItem: BreadcrumbItemWithIndex): void;
}>();

const { categorySections, primaryPropertyType } =
	await useRegionPropertiesLinking(
		props.region,
		props.city,
		props.state,
		props.propertyType
	);

const { trackLinkClicked } = useEvents();

const categoryToFilterMapper = createRoomFilterMapper({
	Quartos: 'bedrooms',
	Suítes: 'suites',
	Vagas: 'parkingSpots',
	Banheiros: 'bathrooms'
} as const);

const handleLinkClick = (
	link: LinkData,
	category: string,
	typology: TypologyData,
	event: Event
) => {
	event.preventDefault();

	const router = useRouter();

	trackLinkClicked({
		section_name: props.sectionName,
		destination_url: router.resolve(link.route).href,
		link_text: link.label,
		link_category: category,
		current_page: 'Listing Properties'
	});

	const propertyTypeEnum = Object.entries(PropertyTypePlurals).find(
		([_, plural]) => plural === typology.type
	)?.[0] as PropertyTypeEnum | undefined;

	const baseFilters = propertyFiltersApi.createLocationFilters({
		regions: props.region,
		cities: props.city,
		states: props.state,
		propertyTypes: propertyTypeEnum
	});

	const categoryType = category as CategoryType;
	const isValidCategory = categoryType in categoryToFilterMapper;
	const finalFilters =
		isValidCategory && link.amount
			? categoryToFilterMapper[categoryType](baseFilters, link.amount)
			: baseFilters;

	emit('filter-link-clicked', finalFilters);
};

const handleBreadcrumbClicked = (breadcrumbItem: BreadcrumbItemWithIndex) => {
	emit('breadcrumbClicked', breadcrumbItem);
};

const hasOptions = computed(() => categorySections.value?.length);
</script>

<template>
	<section v-if="hasOptions" class="flex w-full flex-col">
		<Breadcrumb
			v-if="breadcrumbItems?.length"
			:items="breadcrumbItems"
			@breadcrumb-clicked="handleBreadcrumbClicked"
		/>

		<h2 class="py-6 text-2xl font-medium text-primary md:pt-8 md:text-[32px]">
			Mais opções de {{ primaryPropertyType }} em {{ region }}
		</h2>

		<div class="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-9">
			<div
				v-for="category in categorySections"
				:key="category.key"
				class="flex flex-col gap-4 md:gap-6"
			>
				<h3 class="text-xl font-medium text-primary md:text-2xl">
					{{ category.title }}
				</h3>

				<div
					v-for="typology in category.typologies"
					:key="typology.type"
					class="flex flex-col gap-2 md:gap-4"
				>
					<div
						v-for="link in typology.links"
						:key="`${typology.type}-${link.amount}-${link.route}`"
					>
						<NuxtLink
							:to="link.route"
							@click="handleLinkClick(link, category.title, typology, $event)"
						>
							<TextLink as="p" class="text-primary">
								{{ link.label }}
							</TextLink>
						</NuxtLink>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>
